import { useModuleData } from "@/hooks/useModuleData";
import { KpiCard } from "@/components/common/KpiCard";
import { DataTable } from "@/components/common/DataTable";
import { Loader } from "@/components/common/Loader";
import { eur } from "@/lib/format";

const ACCENT = "#0D9488";
const AGING_COLOR = { "0-7 days": "#10B981", "8-30 days": "#F59E0B", "31+ days": "#EF4444" };

export function CockpitPanel() {
  const { data, loading } = useModuleData("/procurement/cockpit");
  if (loading || !data) return <Loader />;

  const maxSpend = Math.max(1, ...(data.spend_by_vendor || []).map((v) => v.spend));
  const grCols = [
    { key: "po_no", label: "PO", render: (r) => <span className="font-mono text-xs text-[#0D9488]">{r.po_no}</span> },
    { key: "vendor_name", label: "Vendor" },
    { key: "site_id", label: "Site", render: (r) => <span className="font-mono text-xs">{r.site_id}</span> },
    { key: "net_value", label: "Value", render: (r) => <span className="font-mono">{eur(r.net_value)}</span> },
    { key: "status", label: "Status", badge: true },
  ];

  return (
    <div className="space-y-6" data-testid="cockpit-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Open Requisitions" value={data.open_pr_count} accent={ACCENT} testId="cockpit-kpi-open-pr" />
        <KpiCard label="Requisition Value" value={eur(data.open_pr_value)} accent={ACCENT} />
        <KpiCard label="GR-Pending POs" value={data.gr_pending_count} accent="#F97316" />
        <KpiCard label="GR-Pending Value" value={eur(data.gr_pending_value)} accent="#F97316" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-[#e5e9f0] bg-white p-5 shadow-sm" data-testid="cockpit-aging">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Open Requisition Aging</h3>
          <div className="mt-4 space-y-3">
            {(data.pr_aging || []).map((a) => {
              const total = (data.pr_aging || []).reduce((s, x) => s + x.count, 0) || 1;
              return (
                <div key={a.bucket}>
                  <div className="flex justify-between text-xs text-[#6a6d70]"><span>{a.bucket}</span><span className="font-mono">{a.count} · {eur(a.value)}</span></div>
                  <div className="mt-1 h-2 rounded bg-[#eef1f5]"><div className="h-2 rounded" style={{ width: `${Math.round(a.count / total * 100)}%`, background: AGING_COLOR[a.bucket] }} /></div>
                </div>);
            })}
          </div>
        </div>

        <div className="rounded-xl border border-[#e5e9f0] bg-white p-5 shadow-sm" data-testid="cockpit-spend">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Spend by Vendor (Top 8)</h3>
          <div className="mt-4 space-y-2.5">
            {(data.spend_by_vendor || []).map((v) => (
              <div key={v.vendor}>
                <div className="flex justify-between text-xs"><span className="truncate pr-2 text-[#32363a]">{v.vendor}</span><span className="font-mono text-[#6a6d70]">{eur(v.spend)}</span></div>
                <div className="mt-1 h-2 rounded bg-[#eef1f5]"><div className="h-2 rounded bg-[#0D9488]" style={{ width: `${Math.round(v.spend / maxSpend * 100)}%` }} /></div>
              </div>))}
          </div>
        </div>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">GR-Pending Purchase Orders</h3>
        <DataTable testId="cockpit-gr-table" columns={grCols} rows={data.gr_pending || []} empty="No POs awaiting goods receipt." />
      </div>
    </div>
  );
}
